import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';

const TICK_MS = 250;

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Full-screen blocking overlay shown while the local player is downed
// (downedSystem.ts's bleed-out window) - server sends the remaining time
// once (skymp5-client's downedScreenService.ts), the countdown itself then
// ticks client-side purely for display; the server's own timer is what
// actually decides when bleed-out resolves, this is never authoritative.
const DownedScreen: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);
  const [expired, setExpired] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { remainingMs?: number } | null;
      setRemainingMs(Math.max(0, detail?.remainingMs ?? 0));
      setExpired(false);
      setVisible(true);
    };
    const hideHandler = () => {
      setVisible(false);
      setExpired(false);
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    window.addEventListener('skymp5-client:showDownedScreen', showHandler);
    window.addEventListener('skymp5-client:hideDownedScreen', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showDownedScreen', showHandler);
      window.removeEventListener('skymp5-client:hideDownedScreen', hideHandler);
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    intervalRef.current = setInterval(() => {
      setRemainingMs((prev) => {
        const next = prev - TICK_MS;
        if (next <= 0) {
          setExpired(true);
          return 0;
        }
        return next;
      });
    }, TICK_MS);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="downed-screen">
      <div className="downed-screen__vignette" />
      <div className="downed-screen__content">
        <span className="downed-screen__title">Vous êtes à terre</span>
        {!expired ? (
          <>
            <div className="downed-screen__timer-box">
              <span className="downed-screen__timer-label">Temps restant</span>
              <span className="downed-screen__timer">{formatCountdown(remainingMs)}</span>
            </div>
            <span className="downed-screen__message">
              Attendez qu'une personne vous relève. Si ce n'est pas le cas, vous devez appliquer le NLR.
            </span>
          </>
        ) : (
          <span className="downed-screen__message">Vous avez succombé à vos blessures...</span>
        )}
      </div>
    </div>
  );
};

export default DownedScreen;
